// 部門集計表ページ (2026-11-05)
//   旧「部門集計表」(勘定奉行出力 PDF 参考) を freee からライブ生成する。列=部門 / 行=勘定科目(予算実績ページの行構成)。
//   - 期間: 単月 / 期首累計 / 半期累計 / 任意範囲(年月〜年月)
//   - 出力部門: チェックで選択(既定=店舗 11 部門 + 山城屋合算)。グループ小計列(そば/レストラン/カフェ/和食/店舗計)は既定 OFF。
//   - 家賃除外: 地代家賃 / 市場家賃 / 市場家賃振替 を個別に選択(既定=全て除外・行非表示・小計から差引)。表頭に注記。
//   - 印刷: A4 横 / A3 横。1 ページの部門数は用紙幅から自動(手動指定も可)。上段(〜一般管理費)/下段(営業利益〜)で分割。
//     合計列は最初のページ群のみ、科目名列は全ページに表示。全ページに会社名・印刷日付・PAGE を記載。
//   - 整理仕訳 含む/除く は決算書ページと共通トグル(adjustmentMode)。
//   - 金額は freee trial_pl と同じ「税抜」(2026-11-05 実測: 顧問料 2026-03 trial_pl 755,000 = deals amount-vat。税込は 830,500)。
//     ユーザー要件は税込表。税込化には仕訳行単位の消費税額(仕訳帳 generic_v2 / deals+manual_journals)の部門×科目集計が別途必要(未実装)。
//   絵文字禁止 / react-icons/md のみ / @freee_jp/vibes 準拠。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container, PageTitle, CardBase, HStack, Button, Note, Breadcrumbs, MessageBlock, InlineSpinner,
} from '@freee_jp/vibes';
import { MdGridOn, MdRefresh, MdPrint, MdFileDownload, MdWarning } from 'react-icons/md';
import {
  fetchDeptSummary, DeptSummaryResult, DeptColumn, DeptRow, Range, RENT_ACCOUNTS, RentAccount, periodLabelOf,
} from '../api/deptSummary';
import { getIncludeAdjustment, setIncludeAdjustment, adjustNoteFor } from '../api/adjustmentMode';
import { fiscalMonthToCalendar, lastDayOfMonth } from '../types/reports';
import { SECTION, YAMASHIROYA_COMBINED } from '../data/storeGroups';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const COMPANY_NAME = 'クマガイコーポレーション株式会社';

// --- 列定義(ユーザー指定順 2026-11-05) -----------------------------------------
//   既定 ON: La SOFFITTA / 人間関係 / 信州屋渋谷 / 信州屋新宿南口 / BiOcafe / 山城屋庄藏 / 山城屋分店 / 山城屋(合算) /
//            chiocciol@ / 喜乃字屋 / Bistrot Gourmand / 備中屋長左衛門
//   既定 OFF: 本部 / 山城屋EC / ワイン販売課 / VinoBuono / 煙草販売 / デリバリー / その他 / グループ小計列
interface ColDef { key: string; label: string; kind: DeptColumn['kind']; sectionIds: number[]; defaultOn: boolean; }
const COL_DEFS: ColDef[] = [
  { key: 'sec:3068544', label: 'La SOFFITTA', kind: 'section', sectionIds: [SECTION.LaSOFFITTA], defaultOn: true },
  { key: 'sec:3068545', label: '人間関係', kind: 'section', sectionIds: [SECTION.人間関係], defaultOn: true },
  { key: 'sec:3068546', label: '信州屋 渋谷店', kind: 'section', sectionIds: [SECTION.信州屋渋谷], defaultOn: true },
  { key: 'sec:3068547', label: '信州屋 新宿南口店', kind: 'section', sectionIds: [SECTION.信州屋新宿南口], defaultOn: true },
  { key: 'sec:3068548', label: 'BiOcafe', kind: 'section', sectionIds: [SECTION.BiOcafe], defaultOn: true },
  { key: 'sec:3068549', label: '山城屋庄藏', kind: 'section', sectionIds: [SECTION.山城屋庄藏], defaultOn: true },
  { key: 'sec:3068550', label: '山城屋分店', kind: 'section', sectionIds: [SECTION.山城屋分店], defaultOn: true },
  { key: `comb:${YAMASHIROYA_COMBINED}`, label: '山城屋(合算)', kind: 'combined', sectionIds: [SECTION.山城屋庄藏, SECTION.山城屋分店], defaultOn: true },
  { key: 'sec:3068551', label: 'chiocciol@pizzeria', kind: 'section', sectionIds: [SECTION.chiocciola], defaultOn: true },
  { key: 'sec:3068552', label: '喜乃字屋', kind: 'section', sectionIds: [SECTION.喜乃字屋], defaultOn: true },
  { key: 'sec:3068553', label: 'Bistrot Gourmand', kind: 'section', sectionIds: [SECTION.BistrotGourmand], defaultOn: true },
  { key: 'sec:3068554', label: '備中屋長左衛門', kind: 'section', sectionIds: [SECTION.備中屋], defaultOn: true },
  { key: 'sec:3068540', label: '本部', kind: 'section', sectionIds: [SECTION.本部], defaultOn: false },
  { key: 'sec:3095534', label: '山城屋EC', kind: 'section', sectionIds: [SECTION.山城屋EC], defaultOn: false },
  { key: 'sec:3068542', label: 'ワイン販売課', kind: 'section', sectionIds: [SECTION.ワイン販売課], defaultOn: false },
  { key: 'sec:3068541', label: 'VinoBuono', kind: 'section', sectionIds: [SECTION.VinoBuono], defaultOn: false },
  { key: 'sec:3068543', label: '煙草販売', kind: 'section', sectionIds: [SECTION.煙草販売], defaultOn: false },
  { key: 'sec:3095535', label: 'デリバリー', kind: 'section', sectionIds: [SECTION.デリバリー], defaultOn: false },
  { key: 'sec:3068555', label: 'その他', kind: 'section', sectionIds: [SECTION.その他], defaultOn: false },
  // グループ小計列(損益分岐ページの固定グルーピングと同一)
  { key: 'grp:soba', label: 'そば', kind: 'group', sectionIds: [SECTION.信州屋渋谷, SECTION.信州屋新宿南口, SECTION.喜乃字屋], defaultOn: false },
  { key: 'grp:restaurant', label: 'レストラン', kind: 'group', sectionIds: [SECTION.LaSOFFITTA, SECTION.BistrotGourmand, SECTION.chiocciola], defaultOn: false },
  { key: 'grp:cafe', label: 'カフェ', kind: 'group', sectionIds: [SECTION.人間関係, SECTION.BiOcafe], defaultOn: false },
  { key: 'grp:washoku', label: '和食', kind: 'group', sectionIds: [SECTION.備中屋, SECTION.山城屋庄藏, SECTION.山城屋分店], defaultOn: false },
  { key: 'grp:stores', label: '店舗(合計)', kind: 'group', sectionIds: [
    SECTION.信州屋渋谷, SECTION.信州屋新宿南口, SECTION.喜乃字屋, SECTION.LaSOFFITTA, SECTION.BistrotGourmand, SECTION.chiocciola,
    SECTION.人間関係, SECTION.BiOcafe, SECTION.備中屋, SECTION.山城屋庄藏, SECTION.山城屋分店,
  ], defaultOn: false },
];
const TOTAL_COL: DeptColumn = { key: 'total', label: '合計', kind: 'total', sectionIds: [] };

// --- 期間 ---------------------------------------------------------------------
type PeriodMode = 'single' | 'ytd' | 'half' | 'range';
const FISCAL_MONTH_LABEL = ['10月', '11月', '12月', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月'];
/** 今日の属する会計期(80期=2025-10〜2026-09) */
function currentTerm(): number {
  const n = new Date();
  return (n.getMonth() + 1 >= 10 ? n.getFullYear() + 1 : n.getFullYear()) - 1946;
}
/** 直近の完了月(前月)の会計月 */
function lastClosedFiscalMonth(): { term: number; fm: number } {
  const n = new Date();
  const prev = new Date(n.getFullYear(), n.getMonth() - 1, 1);
  const y = prev.getFullYear(); const m = prev.getMonth() + 1;
  const term = (m >= 10 ? y + 1 : y) - 1946;
  const fm = m >= 10 ? m - 9 : m + 3;
  return { term, fm };
}
const ymd = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}-01`;
function resolveRange(mode: PeriodMode, term: number, fm: number, rangeStart: string, rangeEnd: string): Range | null {
  if (mode === 'range') {
    const a = rangeStart.match(/^(\d{4})-(\d{2})$/); const b = rangeEnd.match(/^(\d{4})-(\d{2})$/);
    if (!a || !b) return null;
    const s = { y: Number(a[1]), m: Number(a[2]) }; const e = { y: Number(b[1]), m: Number(b[2]) };
    if (s.y * 12 + s.m > e.y * 12 + e.m) return null;
    return { start: ymd(s.y, s.m), end: lastDayOfMonth(e.y, e.m) };
  }
  const cur = fiscalMonthToCalendar(term, fm);
  const startFm = mode === 'single' ? fm : mode === 'ytd' ? 1 : (fm <= 6 ? 1 : 7);
  const st = fiscalMonthToCalendar(term, startFm);
  return { start: ymd(st.year, st.month), end: lastDayOfMonth(cur.year, cur.month) };
}

// --- 設定の永続化(localStorage) ---------------------------------------------------
const PREF_KEY = 'freeeman.deptSummary.prefs.v1';
interface Prefs { cols?: string[]; rent?: RentAccount[]; paper?: Paper; perPage?: number; hideZero?: boolean; }
const loadPrefs = (): Prefs => { try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); } catch { return {}; } };
const savePrefs = (p: Prefs) => { try { localStorage.setItem(PREF_KEY, JSON.stringify({ ...loadPrefs(), ...p })); } catch { /* */ } };

// --- 用紙・レイアウト(mm) ------------------------------------------------------------
type Paper = 'A4' | 'A3';
const PAPER: Record<Paper, { w: number; h: number }> = { A4: { w: 297, h: 210 }, A3: { w: 420, h: 297 } };
const MARGIN = 8;          // 用紙余白(mm)
const HEADER_H = 24;       // 表頭ブロック高(mm)
const NAME_COL_W = 44;     // 科目名列幅(mm)
const MIN_VAL_COL_W = 25;  // 金額列の最小幅(mm): "▲ 1,234,567,890" が 8pt で収まる幅
const MAX_ROW_H = 4.2;     // 行高上限(mm)
const MIN_ROW_H = 2.7;     // 行高下限(mm)
const MAX_VAL_COL_W = 48;  // 金額列の最大幅(mm): 列数が少ない時に用紙幅いっぱいへ間延びさせない(右側は余白)
const MM_PX = 96 / 25.4;   // 画面プレビューの mm→px 換算(CSS 1mm = 96/25.4px)

const fmtCur = (v: number | undefined): string => {
  if (v == null) return '';
  const r = Math.round(v);
  if (r < 0) return `▲ ${Math.abs(r).toLocaleString('ja-JP')}`;
  return r.toLocaleString('ja-JP');
};

const SEL: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d4d2d0', borderRadius: 4 };
const CHIP = (on: boolean): React.CSSProperties => ({
  padding: '4px 10px', borderRadius: 16, cursor: 'pointer', fontSize: '0.82rem', whiteSpace: 'nowrap',
  border: on ? '1px solid #2563eb' : '1px solid #d4d2d0', background: on ? '#2563eb' : '#fff', color: on ? '#fff' : '#333',
});
const SUBTOTAL_FILL = '#e6e6e6';
const NEW_FILL = '#fee2e2';
const FONT = `"Noto Sans JP","Yu Gothic","Meiryo",sans-serif`;

// --- 1 ページ(シート) -----------------------------------------------------------------
interface SheetSpec { cols: DeptColumn[]; part: 'upper' | 'lower'; pageNo: number; pageTotal: number; }

function Sheet({ spec, result, paper, rows, rowH, fontPt, headerNote }: {
  spec: SheetSpec; result: DeptSummaryResult; paper: Paper; rows: DeptRow[]; rowH: number; fontPt: number; headerNote: string;
}) {
  const P = PAPER[paper];
  const contentW = P.w - MARGIN * 2;
  const valW = Math.min(MAX_VAL_COL_W, (contentW - NAME_COL_W) / spec.cols.length);
  const tableW = NAME_COL_W + valW * spec.cols.length;
  const partRows = rows.filter((r) => r.part === spec.part);
  // border-collapse でも罫線分(約0.2mm)が行高に上乗せされるため、セル高は rowH から罫線分を引いて指定する
  const BORDER = 0.2;
  const cellH = rowH - BORDER;
  const cell: React.CSSProperties = {
    border: `${BORDER}mm solid #000`, padding: '0 1.2mm', height: `${cellH}mm`, lineHeight: `${cellH}mm`,
    fontSize: `${fontPt}pt`, whiteSpace: 'nowrap', overflow: 'hidden', boxSizing: 'border-box',
  };
  return (
    <div className="ds-sheet" style={{
      width: `${P.w}mm`, height: `${P.h}mm`, padding: `${MARGIN}mm`, boxSizing: 'border-box', background: '#fff',
      margin: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.12)', fontFamily: FONT, color: '#000', position: 'relative', overflow: 'hidden',
    }}>
      {/* 表頭 */}
      <div style={{ height: `${HEADER_H}mm`, position: 'relative' }}>
        <div style={{ textAlign: 'center', fontSize: '14pt', letterSpacing: '0.6em', fontWeight: 700, paddingTop: '1mm' }}>部門集計表</div>
        <div style={{ textAlign: 'center', fontSize: '8pt', marginTop: '0.5mm' }}>{periodLabelOf(result.range)}</div>
        <div style={{ position: 'absolute', left: 0, bottom: '6mm', fontSize: '9pt' }}>{COMPANY_NAME}</div>
        <div style={{ position: 'absolute', left: 0, bottom: '1mm', fontSize: '7pt' }}>{headerNote}</div>
        <div style={{ position: 'absolute', right: 0, top: '2mm', fontSize: '8pt', textAlign: 'right', lineHeight: 1.5 }}>
          <div>PAGE: {spec.pageNo} / {spec.pageTotal}</div>
          <div>印刷日付: {result.printedAt}</div>
          <div>【税抜・発生】（単位：円）</div>
        </div>
      </div>
      <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: `${tableW}mm` }}>
        <colgroup>
          <col style={{ width: `${NAME_COL_W}mm` }} />
          {spec.cols.map((c) => (<col key={c.key} style={{ width: `${valW}mm` }} />))}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...cell, background: '#f0f0f0', fontWeight: 400, textAlign: 'left', height: `${MAX_ROW_H}mm`, lineHeight: `${MAX_ROW_H}mm` }}>科目名</th>
            {spec.cols.map((c) => (
              <th key={c.key} style={{ ...cell, background: '#f0f0f0', fontWeight: c.kind === 'total' || c.kind === 'group' ? 700 : 400, textAlign: 'center', height: `${MAX_ROW_H}mm`, lineHeight: `${MAX_ROW_H}mm` }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {partRows.map((r, i) => {
            if (r.kind === 'blank') {
              return (<tr key={i}><td colSpan={spec.cols.length + 1} style={{ ...cell, height: `${rowH * 0.6 - BORDER}mm`, lineHeight: `${rowH * 0.6 - BORDER}mm`, borderLeft: 'none', borderRight: 'none' }} /></tr>);
            }
            const fill = r.kind === 'subtotal' ? SUBTOTAL_FILL : (r.isNew ? NEW_FILL : undefined);
            const bold = r.kind === 'subtotal';
            return (
              <tr key={i}>
                <td className={r.isNew ? 'ds-new' : undefined} style={{ ...cell, background: fill, fontWeight: bold ? 700 : 400, paddingLeft: `${1.2 + r.indent * 2.5}mm` }}>
                  {r.isNew && <span className="ds-new-dot" style={{ color: '#dc2626', marginRight: '1mm', fontSize: '0.7em' }}>●</span>}
                  {r.label}
                </td>
                {spec.cols.map((c) => (
                  <td key={c.key} className={r.isNew ? 'ds-new' : undefined} style={{ ...cell, background: fill, fontWeight: bold ? 700 : 400, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtCur(r.values[c.key])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const printCss = (paper: Paper) => {
  const P = PAPER[paper];
  return `
.ds-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
@media print {
  html { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  html, body { width: ${P.w}mm !important; min-width: 0 !important; max-width: ${P.w}mm !important;
    margin: 0 !important; padding: 0 !important; background: #fff !important; overflow: visible !important; }
  #root, #root > div, .vb-container, .vb-container--responsive {
    width: ${P.w}mm !important; min-width: 0 !important; max-width: ${P.w}mm !important; margin: 0 !important; padding: 0 !important; }
  body * { visibility: hidden; }
  #dept-print, #dept-print * { visibility: visible; }
  .vb-container { position: static !important; }
  .vb-container > :not(#dept-print) { display: none !important; }
  #dept-print { position: absolute; left: 0; top: 0; width: ${P.w}mm !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
  .no-print, .pa-no-print { display: none !important; }
  .vb-globalNavi, nav.vb-globalNavi, [class*="globalNavi"], [class*="GlobalNavi"] { display: none !important; }
  @page { size: ${paper} landscape; margin: 0; }
  .ds-scale { width: auto !important; height: auto !important; margin: 0 !important; overflow: visible !important; }
  .ds-scale-inner { transform: none !important; }
  .ds-sheet { transform: none !important; box-shadow: none !important; margin: 0 !important; width: ${P.w}mm !important; height: ${P.h}mm !important;
    overflow: hidden !important; break-after: page; page-break-after: always; }
  .ds-sheet:last-child { break-after: auto; page-break-after: auto; }
  .ds-new { background: #fff !important; }
  .ds-new-dot { display: none !important; }
}
`;
};

// --- ページ本体 -----------------------------------------------------------------------
export const DeptSummaryPage: React.FC = () => {
  const init = useMemo(() => loadPrefs(), []);
  const lc = useMemo(() => lastClosedFiscalMonth(), []);
  const terms = useMemo(() => { const t = currentTerm(); return [t, t - 1, t - 2, t - 3]; }, []);
  const [mode, setMode] = useState<PeriodMode>('single');
  const [term, setTerm] = useState<number>(lc.term);
  const [fm, setFm] = useState<number>(lc.fm);
  const [rangeStart, setRangeStart] = useState<string>(() => { const c = fiscalMonthToCalendar(lc.term, 1); return `${c.year}-${String(c.month).padStart(2, '0')}`; });
  const [rangeEnd, setRangeEnd] = useState<string>(() => { const c = fiscalMonthToCalendar(lc.term, lc.fm); return `${c.year}-${String(c.month).padStart(2, '0')}`; });
  const [colKeys, setColKeys] = useState<string[]>(() => init.cols || COL_DEFS.filter((c) => c.defaultOn).map((c) => c.key));
  const [rent, setRent] = useState<RentAccount[]>(() => init.rent || [...RENT_ACCOUNTS]);
  const [paper, setPaper] = useState<Paper>(() => init.paper || 'A4');
  const [perPage, setPerPage] = useState<number>(() => init.perPage || 0); // 0=自動
  const [hideZero, setHideZero] = useState<boolean>(() => init.hideZero ?? true);
  const [includeAdj, setIncludeAdj] = useState<boolean>(() => getIncludeAdjustment());
  const [sectionNames, setSectionNames] = useState<Record<number, string>>({});
  const [result, setResult] = useState<DeptSummaryResult | null>(null);
  // 画面プレビュー: 用紙(A4横297mm / A3横420mm)が表示領域より広い場合は縮小して全体を見せる(印刷では等倍)。
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 部門表示名は freee 部門名(long_name)をそのまま使う(静的ラベルは fallback)
  useEffect(() => {
    fetch(`${API_BASE}/api/sections`).then((r) => r.json()).then((d) => {
      const arr: any[] = Array.isArray(d) ? d : (d.sections || d.rows || []);
      const m: Record<number, string> = {};
      for (const s of arr) if (s.section_id && s.long_name) m[Number(s.section_id)] = String(s.long_name);
      setSectionNames(m);
    }).catch(() => { /* fallback: 静的ラベル */ });
  }, []);

  const colDefsNamed = useMemo(() => COL_DEFS.map((c) => (
    c.kind === 'section' && sectionNames[c.sectionIds[0]] ? { ...c, label: sectionNames[c.sectionIds[0]] } : c
  )), [sectionNames]);

  const selectedCols = useMemo<DeptColumn[]>(() => [
    TOTAL_COL,
    ...colDefsNamed.filter((c) => colKeys.includes(c.key)).map((c): DeptColumn => ({ key: c.key, label: c.label, kind: c.kind, sectionIds: c.sectionIds })),
  ], [colDefsNamed, colKeys]);

  const toggleCol = useCallback((key: string) => {
    setColKeys((cur) => { const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]; savePrefs({ cols: next }); return next; });
  }, []);
  const toggleRent = useCallback((a: RentAccount) => {
    setRent((cur) => { const next = cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a]; savePrefs({ rent: next }); return next; });
  }, []);

  const range = useMemo(() => resolveRange(mode, term, fm, rangeStart, rangeEnd), [mode, term, fm, rangeStart, rangeEnd]);

  const generate = useCallback(async () => {
    if (!range) { setError('期間の指定が不正です(開始 ≤ 終了 の年月を指定してください)'); return; }
    if (!colKeys.length) { setError('出力部門が選択されていません'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetchDeptSummary(range, selectedCols, rent);
      setResult(r);
      if (r.meta.empty) setError('指定期間の実績が freee から取得できませんでした(0 件)');
    } catch (e: any) {
      setError(e.message || String(e)); setResult(null);
    } finally { setLoading(false); }
  }, [range, colKeys.length, selectedCols, rent]);

  // --- 表示行(ゼロ行非表示) / ページ割 ---
  const rowsShown = useMemo(() => {
    if (!result) return [];
    const base = hideZero ? result.rows.filter((r) => !(r.kind === 'detail' && r.allZero)) : result.rows;
    // 空行の連続・先頭空行を除去
    return base.filter((r, i, a) => !(r.kind === 'blank' && (i === 0 || a[i - 1].kind === 'blank')));
  }, [result, hideZero]);

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const calc = () => {
      const avail = el.clientWidth;
      const sheetPx = PAPER[paper].w * MM_PX;
      setPreviewScale(avail > 0 ? Math.min(1, avail / sheetPx) : 1);
    };
    calc();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(calc) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', calc);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', calc); };
  }, [paper, result]);

  const layout = useMemo(() => {
    if (!result) return null;
    const P = PAPER[paper];
    const contentW = P.w - MARGIN * 2;
    const autoPer = Math.max(1, Math.floor((contentW - NAME_COL_W) / MIN_VAL_COL_W));
    // 自動時はページ間で列数を均す(13列・上限9 → 9+4 でなく 7+6)。手動指定時はその数で固定。
    const nCols = result.columns.length;
    const per = perPage > 0 ? perPage : Math.max(1, Math.ceil(nCols / Math.max(1, Math.ceil(nCols / autoPer))));
    const chunks: DeptColumn[][] = [];
    for (let i = 0; i < result.columns.length; i += per) chunks.push(result.columns.slice(i, i + per));
    const pageTotal = chunks.length * 2;
    const sheets: SheetSpec[] = [];
    chunks.forEach((cols, ci) => {
      sheets.push({ cols, part: 'upper', pageNo: ci * 2 + 1, pageTotal });
      sheets.push({ cols, part: 'lower', pageNo: ci * 2 + 2, pageTotal });
    });
    // 行高: 上段/下段のうち多い方が 1 ページに収まるよう自動(上限 MAX_ROW_H / 下限 MIN_ROW_H)
    const avail = P.h - MARGIN * 2 - HEADER_H - MAX_ROW_H - 2; // ヘッダー行分 + 安全余白 2mm を引く
    const nUpper = rowsShown.filter((r) => r.part === 'upper').reduce((s, r) => s + (r.kind === 'blank' ? 0.6 : 1), 0);
    const nLower = rowsShown.filter((r) => r.part === 'lower').reduce((s, r) => s + (r.kind === 'blank' ? 0.6 : 1), 0);
    const n = Math.max(nUpper, nLower, 1);
    const rowH = Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, avail / n));
    const fontPt = Math.min(8.5, Math.round(rowH * 2.1 * 10) / 10);
    return { sheets, rowH, fontPt, per, autoPer, overflow: avail / n < MIN_ROW_H };
  }, [result, paper, perPage, rowsShown]);

  const headerNote = useMemo(() => {
    const adj = adjustNoteFor();
    const r = result?.excludedRent ?? rent;
    const rentNote = r.length ? `【家賃除外】${r.join('・')} を除外（行非表示・販売経費以下の小計から差引）` : '【家賃】除外なし';
    return `${adj}　${rentNote}`;
  }, [result, rent]);

  const savePdf = useCallback(() => {
    if (!result) return;
    const prevTitle = document.title;
    document.title = `部門集計表 ${result.range.start}〜${result.range.end}`;
    const restore = () => { document.title = prevTitle; window.removeEventListener('afterprint', restore); };
    window.addEventListener('afterprint', restore);
    window.print();
  }, [result]);

  return (
    <div style={{ background: '#f3f1ef', minHeight: '100vh', paddingBottom: '3rem' }}>
      <style>{printCss(paper)}</style>
      <Container>
        <Breadcrumbs links={[{ url: '#', title: 'FREEEMAN' }, { url: '#dept_summary', title: '部門集計表' }]} />
        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <HStack gap={2} alignItems="center">
            <MdGridOn size={28} color="#2563eb" />
            <PageTitle>部門集計表</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Note>
            部門ごとに各勘定科目の合計(発生額)を freee 試算表から取得して一覧表にします。金額は freee 試算表と同じ税抜です(税込化は未対応・検討中)。行は部門別予算実績ページの科目構成、列は選択した部門です。
            家賃(地代家賃・市場家賃・市場家賃振替)は既定で除外し、小計からも差し引きます。印刷は A4 横 / A3 横で、1 ページの部門数は用紙幅から自動、上段(〜一般管理費)/下段(営業利益〜)に分割します。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: 16 }} className="no-print">
            {/* 期間 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              <div style={{ display: 'inline-flex', border: '1px solid #d4d2d0', borderRadius: 4, overflow: 'hidden' }} title="集計期間の種類">
                {([['single', '単月'], ['ytd', '期首累計'], ['half', '半期累計'], ['range', '任意範囲']] as [PeriodMode, string][]).map(([k, l], i) => (
                  <button key={k} onClick={() => setMode(k)} style={{ padding: '6px 12px', border: 'none', borderLeft: i ? '1px solid #d4d2d0' : 'none', cursor: 'pointer', background: mode === k ? '#2563eb' : '#fff', color: mode === k ? '#fff' : '#333', fontSize: '0.85rem' }}>{l}</button>
                ))}
              </div>
              {mode !== 'range' ? (
                <>
                  <select value={term} onChange={(e) => setTerm(Number(e.target.value))} style={SEL} title="会計期">
                    {terms.map((t) => (<option key={t} value={t}>{t}期</option>))}
                  </select>
                  <select value={fm} onChange={(e) => setFm(Number(e.target.value))} style={SEL} title={mode === 'single' ? '対象月度' : '累計の終了月度'}>
                    {FISCAL_MONTH_LABEL.map((lbl, i) => (<option key={i} value={i + 1}>{i + 1}月度（{lbl}）</option>))}
                  </select>
                </>
              ) : (
                <>
                  <input type="month" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} style={SEL} title="開始年月" />
                  <span>〜</span>
                  <input type="month" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} style={SEL} title="終了年月" />
                </>
              )}
              {range && <span style={{ fontSize: '0.85rem', color: '#555' }}>{range.start} ～ {range.end}</span>}
              <select value={includeAdj ? '1' : '0'} onChange={(e) => { const v = e.target.value === '1'; setIncludeAdjustment(v); setIncludeAdj(v); }} style={SEL}
                title="決算整理仕訳の扱い(決算書ページと共通)">
                <option value="1">整理仕訳 含む</option>
                <option value="0">整理仕訳 除く</option>
              </select>
            </div>

            {/* 出力部門 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 72 }}>出力部門</span>
              {colDefsNamed.filter((c) => c.kind !== 'group').map((c) => (
                <button key={c.key} onClick={() => toggleCol(c.key)} style={CHIP(colKeys.includes(c.key))}>{c.label}</button>
              ))}
              <button onClick={() => { const next = COL_DEFS.filter((c) => c.defaultOn).map((c) => c.key); setColKeys(next); savePrefs({ cols: next }); }}
                style={{ ...CHIP(false), borderStyle: 'dashed' }}>既定に戻す</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 72 }}>グループ小計</span>
              {colDefsNamed.filter((c) => c.kind === 'group').map((c) => (
                <button key={c.key} onClick={() => toggleCol(c.key)} style={CHIP(colKeys.includes(c.key))}>{c.label}</button>
              ))}
            </div>

            {/* 家賃除外 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 72 }}>家賃除外</span>
              {RENT_ACCOUNTS.map((a) => (
                <label key={a} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={rent.includes(a)} onChange={() => toggleRent(a)} /> {a}
                </label>
              ))}
              <span style={{ fontSize: '0.78rem', color: '#777' }}>チェックした科目は行を出さず、販売経費以下の小計からも差し引きます</span>
            </div>

            {/* 印刷設定 + 操作 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 72 }}>印刷</span>
              <select value={paper} onChange={(e) => { const p = e.target.value as Paper; setPaper(p); savePrefs({ paper: p }); }} style={SEL} title="用紙">
                <option value="A4">A4 横</option>
                <option value="A3">A3 横</option>
              </select>
              <select value={perPage} onChange={(e) => { const v = Number(e.target.value); setPerPage(v); savePrefs({ perPage: v }); }} style={SEL} title="1ページの部門数">
                <option value={0}>1ページの部門数: 自動{layout ? `（${layout.autoPer}）` : ''}</option>
                {[3, 4, 5, 6, 8, 10, 12, 15].map((n) => (<option key={n} value={n}>1ページの部門数: {n}</option>))}
              </select>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={hideZero} onChange={(e) => { setHideZero(e.target.checked); savePrefs({ hideZero: e.target.checked }); }} /> 全列 0 の科目を省く
              </label>
              <Button appearance="primary" onClick={generate} disabled={loading}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 56, justifyContent: 'center' }}>
                  <span style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loading ? <InlineSpinner isLoading={true} /> : <MdRefresh size={16} />}
                  </span>
                  生成
                </span>
              </Button>
              <Button onClick={() => window.print()} disabled={!result}><MdPrint /> 印刷</Button>
              <Button appearance="primary" onClick={savePdf} disabled={!result}><MdFileDownload /> PDFダウンロード</Button>
            </div>
          </div>

          {error && (<div style={{ padding: '0 16px 16px' }} className="no-print"><MessageBlock error>{error}</MessageBlock></div>)}
          {result && !result.meta.empty && (
            <div style={{ padding: '0 16px 16px' }} className="no-print">
              {result.meta.newAccounts.length > 0 && (
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d', marginBottom: 4 }}>
                  <MdWarning style={{ verticalAlign: 'middle' }} /> テンプレ外の勘定科目を所属カテゴリーの小計直前に自動挿入しました(画面では薄赤・印刷では通常表示): {result.meta.newAccounts.join('、')}
                </div>
              )}
              {result.meta.checks.length > 0 && (
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d', marginBottom: 4 }}>
                  <MdWarning style={{ verticalAlign: 'middle' }} /> 合計列の小計が freee の集計値と一致しません:
                  {result.meta.checks.map((c) => ` ${c.label}（表 ${fmtCur(c.computed)} / freee ${fmtCur(c.freee)}）`).join('、')}
                </div>
              )}
              {result.meta.unassigned.length > 0 && (
                <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: 4 }}>
                  部門未設定の金額があります(合計列にのみ含まれます): {result.meta.unassigned.map((u) => `${u.label} ${fmtCur(u.amount)}`).join('、')}
                </div>
              )}
              {layout?.overflow && (
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d' }}>
                  <MdWarning style={{ verticalAlign: 'middle' }} /> 行数が多く 1 ページに収まりきらない可能性があります。A3 を選ぶか「全列 0 の科目を省く」を有効にしてください。
                </div>
              )}
              <div style={{ fontSize: '0.82rem', color: '#555' }}>
                印刷プレビュー: {layout ? `${layout.sheets.length} ページ（${paper} 横・1ページ ${layout.per} 列・上段/下段）` : ''}
              </div>
            </div>
          )}
        </CardBase>

        {result && !result.meta.empty && layout && (
          <div id="dept-print" className="ds-print" ref={previewRef} style={{ marginTop: 18 }}>
            {layout.sheets.map((s) => (
              <div key={`${s.pageNo}`} className="ds-scale" style={{
                width: `${PAPER[paper].w * MM_PX * previewScale}px`, height: `${PAPER[paper].h * MM_PX * previewScale}px`,
                margin: '0 auto 18px', overflow: 'hidden',
              }}>
                <div className="ds-scale-inner" style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: `${PAPER[paper].w}mm` }}>
                  <Sheet spec={s} result={result} paper={paper} rows={rowsShown} rowH={layout.rowH} fontPt={layout.fontPt} headerNote={headerNote} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default DeptSummaryPage;
